import React, { useEffect, Fragment, useState } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSpinner } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, Transition, Listbox } from "@headlessui/react";
import { Formik, Form, Field } from "formik";
import ErrorBoundary from "../../containers/ErrorBoundary";

import moment from "moment";
import NotificationToast from "../Helpers/NotificationToast";
import SubmitButton from "../Forms/SubmitButton";
import SettingsDateTimePicker from "../Forms/SettingsDateTimePicker";
import { getBooking } from "../../store/booking/bookingActions";
import { getStaff } from "../../store/auth/authActions";
import ProfilePicture from "../Auth/ProfilePicture";
import SettingsSwitch from "../Forms/SettingsSwitch";

export default function ModalMove(props) {
  const dispatch = useDispatch();
  const [open] = useState(true);
  const { staff } = useSelector((state) => state.auth);
  const { current_booking } = useSelector((state) => state.booking);
  const [isLoading, setIsLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [beds, setBeds] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        // Load all necessary data for the move booking modal
        const [locationsRes, propertiesRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}locations`),
          axios.get(`${process.env.REACT_APP_API_URL}properties`),
        ]);

        setLocations(locationsRes.data || []);
        setProperties(propertiesRes.data || []);
      } catch (error) {
        console.error("Failed to load move booking data:", error);
        toast(<NotificationToast title="Error" message="Failed to load data" type="error" />);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed z-50 inset-0 overflow-y-auto"
        open={open}
        onClose={() => !isLoading && dispatch(closeModal())}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <ErrorBoundary>
              <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-auto shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6 relative">
                {/* Loading Overlay */}
                {isLoading && (
                  <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center z-10 rounded-lg">
                    <div className="text-center">
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="h-12 w-12 text-primary animate-spin mb-4"
                        aria-hidden="true"
                      />
                      <p className="text-gray-600 font-medium">Loading Move Booking data...</p>
                      <p className="text-sm text-gray-500 mt-2">Please wait while we fetch the necessary information</p>
                    </div>
                  </div>
                )}

                {/* Close button - disabled while loading */}
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4 z-20">
                  <button
                    type="button"
                    className={`bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0 ${
                      isLoading ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={() => !isLoading && dispatch(closeModal())}
                    disabled={isLoading}
                  >
                    <span className="sr-only">Close</span>
                    <FontAwesomeIcon
                      className="h-6 w-6"
                      aria-hidden="true"
                      icon={faTimes}
                    />
                  </button>
                </div>

                {/* Modal Content - blurred while loading */}
                <div className={isLoading ? "filter blur-sm pointer-events-none" : ""}>
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900 mb-4">
                        Move Booking
                      </Dialog.Title>
                      
                      {/* Add your modal content here */}
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          Move booking functionality will be displayed here once data is loaded.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ErrorBoundary>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
